import React, {
  type FC,
  type ReactElement,
  type ReactNode,
  useEffect,
} from "react";
import ReactDOM from "react-dom";
import { IoClose } from "react-icons/io5";

import tailwindConfig from "@root/tailwind.config";
import { cn, defaultRipple } from "@utilities/helper";
import { AnimatePresence, motion } from "motion/react";
import resolveConfig from "tailwindcss/resolveConfig";

const Modal: FC<{
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  bgColor?: string;
  title?: string;
  isCloseButtonExist?: boolean;
  closeOnBackgroundClick?: boolean;
  isContentScrollable?: boolean;
  className?: string;
  /**
   * Classes for the outer positioning box (margins, max-width). Vertical
   * offsets belong here rather than in `className`, so the badge stays
   * aligned with the panel's top edge.
   */
  wrapperClassName?: string;
  titleClassName?: string;
  /**
   * Rendered outside the scrollable panel so it can overhang the modal's top
   * edge without being clipped. Position it against the panel box with
   * `absolute` + `top-0`.
   */
  badge?: ReactNode;
  children?: any;
}> = ({
  isModalOpen,
  setIsModalOpen,
  bgColor = "#fff",
  title = "",
  isCloseButtonExist = true,
  closeOnBackgroundClick = true,
  isContentScrollable = false,
  className = "",
  wrapperClassName = "",
  titleClassName = "",
  badge,
  children,
}): ReactElement => {
  const isSSR =
    typeof document === "undefined" && typeof window === "undefined";
  let backgroundColor: string;
  let closeBtnColor: string;
  switch (bgColor) {
    case "primary":
      backgroundColor =
        resolveConfig(tailwindConfig).theme.colors.primary.DEFAULT;
      closeBtnColor = resolveConfig(tailwindConfig).theme.colors.white;
      break;
    case "secondary":
      backgroundColor =
        resolveConfig(tailwindConfig).theme.colors.secondary.DEFAULT;
      closeBtnColor = resolveConfig(tailwindConfig).theme.colors.white;
      break;
    default:
      backgroundColor = bgColor;
      closeBtnColor = resolveConfig(tailwindConfig).theme.colors.gray[400];
      break;
  }
  useEffect(() => {
    if (!isSSR) {
      const toggleOverflow = (value: boolean): void => {
        const action = value ? "add" : "remove";
        document.body.classList[action]("overflow-hidden");
        document.documentElement.classList[action]("overflow-hidden");
      };

      isModalOpen ? toggleOverflow(true) : toggleOverflow(false);

      return () => {
        toggleOverflow(false);
      };
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isSSR || !isModalOpen) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen, isSSR]);

  return (
    <>
      {!isSSR &&
        ReactDOM.createPortal(
          <AnimatePresence>
            {isModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, backdropFilter: "blur(10px)" }}
                exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-50 flex h-dvh items-center justify-center bg-gray-500/75 p-4 selection:bg-primary/80 selection:text-white md:p-6"
                aria-labelledby={title ?? ""}
                role="dialog"
                aria-modal="true"
                onClick={() => {
                  if (closeOnBackgroundClick) {
                    setIsModalOpen(false);
                  }
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: "10%", scale: 0.9 }}
                  animate={{
                    opacity: 1,
                    y: "0%",
                    scale: 1,
                  }}
                  exit={{
                    opacity: 0,
                    y: "10%",
                    scale: 0.9,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 15,
                  }}
                  className={cn(
                    "pointer-events-auto relative flex w-full flex-col items-center sm:mx-2 sm:my-8 sm:max-w-lg md:max-w-[720px] xl:max-w-screen-xl",
                    wrapperClassName,
                  )}
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  {/* Sibling of the scrollable panel: `overflow-y-auto` below
                      also clips the x axis, so anything overhanging the panel's
                      edges has to live out here. */}
                  {badge}
                  <div
                    className={cn(
                      "relative flex max-h-[90dvh] w-full flex-col items-center gap-2 overflow-y-auto rounded-2xl p-4 text-start shadow-xl sm:max-h-[calc(100dvh-4rem)] md:p-8",
                      className,
                    )}
                    style={{ backgroundColor }}
                  >
                    {(isCloseButtonExist || title) && (
                      <div className="flex w-full flex-row items-center justify-between">
                        <div></div>
                        <div
                          className={cn(
                            "ms-10 text-xl font-bold",
                            titleClassName,
                          )}
                        >
                          {title}
                        </div>
                        <div>
                          {isCloseButtonExist && (
                            <button
                              ref={defaultRipple}
                              className={cn(
                                "size-10 rounded-full border border-transparent p-0 outline-none transition duration-300 hover:rotate-90 hover:opacity-90",
                                {
                                  "focus:custom-muted-focus-ring":
                                    closeBtnColor ===
                                    resolveConfig(tailwindConfig).theme.colors
                                      .gray[400],
                                  "focus:ring-2 focus:ring-white/80 focus:ring-offset-1":
                                    closeBtnColor !==
                                    resolveConfig(tailwindConfig).theme.colors
                                      .gray[400],
                                },
                              )}
                              onClick={() => {
                                setIsModalOpen(false);
                              }}
                            >
                              <IoClose
                                className="size-full"
                                style={{
                                  color: closeBtnColor,
                                }}
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    <div
                      className={cn("relative block", {
                        "-mx-4 -mb-4 -mt-2 max-h-[75dvh] w-[calc(100%+2rem)] overflow-y-auto px-4 pb-4 pt-2 md:-mx-8 md:-mb-8 md:w-[calc(100%+4rem)] md:px-8 md:pb-8":
                          isContentScrollable,
                        "w-full": !isContentScrollable,
                      })}
                    >
                      {children}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
};

export default Modal;
